'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { Globe2 } from 'lucide-react'
import { useTheme } from 'next-themes'
import type { createSignInGlobe } from '@/lib/sign-in-globe'
import styles from './sign-in-screen.module.css'

export default function LearningGlobe() {
  const canvas = useRef<HTMLCanvasElement>(null)
  const control = useRef<HTMLButtonElement>(null)
  const instructions = useId()
  const renderer = useRef<ReturnType<typeof createSignInGlobe>>()
  const [ready, setReady] = useState(false)
  const { resolvedTheme } = useTheme()

  useEffect(() => {
    let disposed = false
    setReady(false)
    // Keep WebGL out of the server render and the sign-in button's critical path.
    import('@/lib/sign-in-globe').then(({ createSignInGlobe }) => {
      if (disposed || !canvas.current || !control.current) return
      try {
        renderer.current = createSignInGlobe(canvas.current, resolvedTheme !== 'light', control.current)
        setReady(true)
      } catch {
        // The existing icon is the static fallback when WebGL is unavailable.
        setReady(false)
      }
    }).catch(() => { if (!disposed) setReady(false) })
    return () => { disposed = true; renderer.current?.destroy(); renderer.current = undefined }
  }, [resolvedTheme])

  return (
    <div className={styles.globe}>
      <button ref={control} type='button' className={styles.globeArt}
        disabled={!ready} aria-label='Rotate globe' aria-describedby={instructions}>
        {!ready && <Globe2 className={styles.globeFallback} strokeWidth={.75} aria-hidden='true' />}
        <canvas ref={canvas} className={styles.globeCanvas} data-ready={ready} aria-hidden='true' />
      </button>
      <span id={instructions} className='sr-only'>
        Drag horizontally or use the left and right arrow keys to rotate. Press Enter for a small turn.
      </span>
    </div>
  )
}
